/**
 * PaginatedReportTable
 *
 * Server-side paginated / sortable / filterable table for a Report DSL that
 * targets ClickHouse via `POST /il/analytics/:tenant/:site/report-query`.
 *
 * Responsibilities:
 *   • Derive column metadata from `report.query` (bucket + dimensions + metrics).
 *   • Every click on a header toggles server-side sort (ASC ↔ DESC).
 *   • Dimension columns expose a debounced inline "contains" filter which is
 *     sent to the server as an extra `runtimeFilters` entry.
 *   • Pagination uses MUI `TablePagination` + server `pagination: {page, pageSize}`.
 *   • Does its own data fetch — parent (ReportPanel) should not fetch for
 *     `type: 'table'` to avoid duplicate ClickHouse requests.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Button, IconButton, Tooltip,
  Typography, CircularProgress, Alert, Chip, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, TablePagination, TextField, InputAdornment, Paper, useTheme,
} from '@mui/material';
import {
  Refresh, FilterAlt, Search, Clear, Download, EditOutlined,
} from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import { ReportDefinition, TimeRangeDef, FilterDef } from '../Settings/reportDsl';
import {
  buildReportTableCsv,
  downloadCsvFile,
  REPORT_TABLE_EXPORT_MAX_ROWS,
  sanitizeCsvFilename,
} from './exportReportTableCsv';
import { panelMinHeightPx } from './chartTimeRange';
import { reportsPanelToolbarButtonSx, reportsToolbarSurfaceBg } from './reportsToolbarSurface';
import {
  renderFormattedTableCell,
  resolveTableColumnDisplayAs,
  type TableColumnDisplayAs,
} from './reportTableCellDisplay';
import { StorageQuotaNoStats } from './DamStorageQuotaBar';
import { getInfralytiqsReportTableHeaderCellSx } from './infralytiqsReportTableTheme';
import { formatEventTimestampLocal } from './eventTimeDisplay';
import { ReportPanelApiAction } from './ReportPanelApiAction';

/** Metrics that store a timestamp (ISO string, ClickHouse DateTime, or epoch millis). */
const isTimestampMetricField = (field: string | undefined): boolean => {
  if (!field) return false;
  if (field === 'event_time') return true;
  const last = field.split('.').pop() || field;
  return last === 'started_at' || last === 'ended_at'
    || last === 'started_at_ms' || last === 'ended_at_ms'
    || last === 'started_at' || last === 'ended_at'
    || last === 'started_at_ms' || last === 'ended_at_ms'
    || last === 'migrated_at' || last === 'migrated_at_ms';
};

interface ColumnMeta {
  /** Key used to address the field on the row object (dim_0 / metric_0 / bucket). */
  key: string;
  label: string;
  kind: 'bucket' | 'dimension' | 'metric';
  /** Source column from the DSL, only present for dimensions (used for filtering). */
  field?: string;
  /** Sort key understood by ReportQueryBuilder: 'time' | 'dim:<i>' | 'metric:<i>'. */
  sortKey: string;
  /** Dimensions + bucket support per-column `contains` filtering via WHERE. */
  filterable: boolean;
  /** Render as number (toLocaleString) when true. */
  numeric: boolean;
  /** Source DSL field (dimensions + metrics) for display formatting. */
  sourceField?: string;
  /** UI-only cell renderer (CSV export uses raw values). */
  displayAs?: TableColumnDisplayAs;
}

interface PaginatedReportTableProps {
  report: ReportDefinition;
  tenantId: string;
  siteId?: string;
  timeRangeOverride?: TimeRangeDef;
  /** External (drill-down) filters merged into every request. */
  runtimeFilters?: FilterDef[];
  onRowClick?: (row: Record<string, any>, report: ReportDefinition) => void;
  /** Header chip shown when the panel is locked to a drill-down value. */
  contextBadge?: string;
  defaultPageSize?: number;
  rowsPerPageOptions?: number[];
  /** IANA timezone for ClickHouse bucketing (see `ReportPanelProps.timezone`). */
  timezone?: string;
  /** Show CSV download next to refresh (default true). */
  enableCsvDownload?: boolean;
  /** When true, omit the card title row (parent supplies context). */
  hideHeader?: boolean;
  /** When provided, shows an edit icon in the header to configure the panel. */
  onEdit?: (report: ReportDefinition) => void;
  /** When false, hide the panel ingest-API (API Specs) button. Defaults to true. */
  showApiAction?: boolean;
}

export const PaginatedReportTable: React.FC<PaginatedReportTableProps> = ({
  report,
  tenantId,
  siteId,
  timeRangeOverride,
  runtimeFilters,
  onRowClick,
  contextBadge,
  defaultPageSize = 20,
  rowsPerPageOptions = [10, 20, 50, 100],
  timezone,
  enableCsvDownload = true,
  hideHeader = false,
  onEdit,
  showApiAction = true,
}) => {
  const theme = useTheme();
  const vis = report.visualizations?.[0] || { type: 'table' };
  const columnDisplay = vis.options?.columnDisplay as Record<string, TableColumnDisplayAs> | undefined;
  const hiddenColumns = (vis.options?.hiddenColumns as string[] | undefined) || undefined;
  const pathSite = useMemo(() => infralytiqsAnalyticsSitePathSegment(siteId), [siteId]);

  const columns: ColumnMeta[] = useMemo(() => {
    const hidden = new Set(hiddenColumns || []);
    const list: ColumnMeta[] = [];
    if (report.query.timeRange?.granularity) {
      list.push({
        key: 'bucket',
        label: 'Bucket',
        kind: 'bucket',
        sortKey: 'time',
        filterable: false,
        numeric: false,
      });
    }
    (report.query.dimensions || []).forEach((d, i) => {
      if (hidden.has(d.field)) return;
      list.push({
        key: `dim_${i}`,
        label: d.label || d.field,
        kind: 'dimension',
        field: d.field,
        sourceField: d.field,
        sortKey: `dim:${i}`,
        filterable: true,
        numeric: false,
        displayAs: resolveTableColumnDisplayAs(d.field, columnDisplay),
      });
    });
    if (!vis.options?.hideMetrics) {
      (report.query.metrics || []).forEach((m, i) => {
        if (hidden.has(m.field)) return;
        const isEventTime = isTimestampMetricField(m.field);
        list.push({
          key: `metric_${i}`,
          label: m.label || `metric_${i}`,
          kind: 'metric',
          sourceField: m.field,
          sortKey: `metric:${i}`,
          filterable: false,
          numeric: !isEventTime && !(m.field === 'user_id' && m.fn === 'argMax'),
          displayAs: resolveTableColumnDisplayAs(m.field, columnDisplay),
        });
      });
    }
    return list;
  }, [report.query, vis.options?.hideMetrics, columnDisplay, hiddenColumns]);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');

  // Raw input state (updates per keystroke) + debounced state (drives fetches)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [debouncedFilters, setDebouncedFilters] = useState<Record<string, string>>({});

  // Metric columns are aggregates, so they filter via HAVING (numeric "≥ min")
  // rather than the WHERE-based `contains` used by dimension columns.
  const [metricFilters, setMetricFilters] = useState<Record<string, string>>({});
  const [debouncedMetricFilters, setDebouncedMetricFilters] = useState<Record<string, string>>({});

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedFilters(columnFilters), 350);
    return () => clearTimeout(id);
  }, [columnFilters]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedMetricFilters(metricFilters), 350);
    return () => clearTimeout(id);
  }, [metricFilters]);

  // Reset to first page whenever a filter or the sort changes. Without this
  // a user on page 7 filtering to 3 results would never see anything.
  useEffect(() => {
    setPage(0);
  }, [debouncedFilters, debouncedMetricFilters, sortKey, sortDir]);

  const effectiveRuntimeFilters = useMemo<FilterDef[]>(() => {
    const out: FilterDef[] = [...(runtimeFilters || [])];
    for (const col of columns) {
      if (!col.filterable || !col.field) continue;
      const v = (debouncedFilters[col.key] || '').trim();
      if (!v) continue;
      out.push({ field: col.field, op: 'contains', value: v });
    }
    return out;
  }, [runtimeFilters, columns, debouncedFilters]);

  const runtimeOrderBy = useMemo(() => {
    if (!sortKey) return undefined;
    return [{ field: sortKey, dir: sortDir }];
  }, [sortKey, sortDir]);

  // Per-column metric filters → HAVING clauses (e.g. "Download count ≥ 5").
  const runtimeHaving = useMemo<FilterDef[]>(() => {
    const out: FilterDef[] = [];
    for (const col of columns) {
      if (col.kind !== 'metric') continue;
      const raw = (debouncedMetricFilters[col.key] || '').trim();
      if (!raw) continue;
      const num = Number(raw);
      if (!Number.isFinite(num)) continue;
      out.push({ field: col.sortKey, op: 'gte', value: num });
    }
    return out;
  }, [columns, debouncedMetricFilters]);

  const fetchData = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, any> = {
        query: report.query,
        runtimeFilters: effectiveRuntimeFilters,
        timeRangeOverride,
        pagination: { page, pageSize },
      };
      if (runtimeOrderBy) body.runtimeOrderBy = runtimeOrderBy;
      if (runtimeHaving.length > 0) body.runtimeHaving = runtimeHaving;
      if (timezone) body.timezone = timezone;

      const res: any = await apiClient.post(
        `/il/analytics/${tenantId}/${pathSite}/report-query`,
        body,
      );
      setRows(res?.data ?? []);
      const tc = res?.totalCount;
      setTotal(typeof tc === 'number' && tc >= 0 ? tc : (res?.rowCount ?? 0));
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [
    report.query, tenantId, pathSite, timeRangeOverride,
    effectiveRuntimeFilters, runtimeOrderBy, runtimeHaving, page, pageSize, timezone,
  ]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fetchAllRowsForExport = useCallback(async (): Promise<Record<string, unknown>[]> => {
    if (!tenantId) return [];
    const exportPageSize = 500;
    const maxRows = Math.min(
      total > 0 ? total : REPORT_TABLE_EXPORT_MAX_ROWS,
      REPORT_TABLE_EXPORT_MAX_ROWS,
    );
    const allRows: Record<string, unknown>[] = [];
    let pageIndex = 0;

    while (allRows.length < maxRows) {
      const body: Record<string, unknown> = {
        query: {
          ...report.query,
          limit: Math.min(maxRows, REPORT_TABLE_EXPORT_MAX_ROWS),
        },
        runtimeFilters: effectiveRuntimeFilters,
        timeRangeOverride,
        pagination: { page: pageIndex, pageSize: exportPageSize },
      };
      if (runtimeOrderBy) body.runtimeOrderBy = runtimeOrderBy;
      if (runtimeHaving.length > 0) body.runtimeHaving = runtimeHaving;
      if (timezone) body.timezone = timezone;

      const res: { data?: Record<string, unknown>[]; totalCount?: number; rowCount?: number } =
        await apiClient.post(`/il/analytics/${tenantId}/${pathSite}/report-query`, body);

      const batch = res?.data ?? [];
      if (batch.length === 0) break;
      allRows.push(...batch);
      if (batch.length < exportPageSize) break;
      if (allRows.length >= maxRows) break;
      pageIndex += 1;
    }

    return allRows.slice(0, maxRows);
  }, [
    tenantId,
    pathSite,
    report.query,
    effectiveRuntimeFilters,
    timeRangeOverride,
    runtimeOrderBy,
    runtimeHaving,
    timezone,
    total,
  ]);

  const handleDownloadCsv = useCallback(async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const exportRows = await fetchAllRowsForExport();
      if (exportRows.length === 0) {
        setDownloadError('No rows to export for the current filters.');
        return;
      }
      const csvColumns = columns.map((c) => ({ key: c.key, label: c.label }));
      const csv = buildReportTableCsv(csvColumns, exportRows);
      const title = vis.title || report.name;
      downloadCsvFile(csv, sanitizeCsvFilename(title));
      if (total > REPORT_TABLE_EXPORT_MAX_ROWS) {
        setDownloadError(
          `Exported first ${REPORT_TABLE_EXPORT_MAX_ROWS.toLocaleString()} of ${total.toLocaleString()} rows (server limit).`,
        );
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'CSV export failed';
      setDownloadError(message);
    } finally {
      setDownloading(false);
    }
  }, [fetchAllRowsForExport, columns, vis.title, report.name, total]);

  const handleSort = (sk: string) => {
    if (sortKey === sk) {
      setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortKey(sk);
      setSortDir('DESC');
    }
  };

  const clearFilter = (colKey: string) => {
    setColumnFilters((f) => {
      const next = { ...f };
      delete next[colKey];
      return next;
    });
  };

  const clearMetricFilter = (colKey: string) => {
    setMetricFilters((f) => {
      const next = { ...f };
      delete next[colKey];
      return next;
    });
  };

  const clickable = Boolean(onRowClick && report.interactions?.onRowClick);

  const panelTitle = vis.title || report.name;
  const panelMinHeight = panelMinHeightPx(report.layout?.h);
  const hasColumnFilters = useMemo(
    () => Object.values(debouncedFilters).some((v) => v.trim())
      || Object.values(debouncedMetricFilters).some((v) => v.trim()),
    [debouncedFilters, debouncedMetricFilters],
  );
  const hasExternalFilters = (runtimeFilters || []).length > 0;
  const isEmptyDataset = !loading && !error && total === 0 && !hasColumnFilters && !hasExternalFilters;

  const toolbarIconButtonSx = reportsPanelToolbarButtonSx(theme, {
    minWidth: 36,
    width: 36,
    height: 36,
    p: 0,
    borderRadius: 1,
  });

  const panelToolbarButtonColor = theme.palette.mode === 'light' ? undefined : 'secondary' as const;

  const headerActions = (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
      {!hideHeader && (
        <ReportPanelApiAction
          report={report}
          tenantId={tenantId}
          siteId={siteId}
          runtimeFilters={runtimeFilters}
          visible={showApiAction}
        />
      )}
      {onEdit && !hideHeader && (
        <Tooltip title="Edit panel configuration">
          <Button
            variant="outlined"
            color={panelToolbarButtonColor}
            size="small"
            onClick={(e) => { e.stopPropagation(); onEdit(report); }}
            aria-label="Edit report panel"
            sx={toolbarIconButtonSx}
          >
            <EditOutlined fontSize="small" />
          </Button>
        </Tooltip>
      )}
      {enableCsvDownload && (
        <Tooltip title={downloading ? 'Preparing CSV…' : 'Download all rows as CSV'}>
          <span>
            <Button
              variant="outlined"
              color={panelToolbarButtonColor}
              size="small"
              onClick={() => void handleDownloadCsv()}
              disabled={loading || downloading || !tenantId || isEmptyDataset}
              aria-label="Download CSV"
              sx={toolbarIconButtonSx}
            >
              {downloading ? <CircularProgress size={18} /> : <Download fontSize="small" />}
            </Button>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Refresh">
        <Button
          variant="outlined"
          color={panelToolbarButtonColor}
          size="small"
          onClick={fetchData}
          disabled={downloading}
          aria-label="Refresh report"
          sx={toolbarIconButtonSx}
        >
          <Refresh fontSize="small" />
        </Button>
      </Tooltip>
    </Stack>
  );

  const renderCell = (row: Record<string, any>, col: ColumnMeta) => {
    const raw = row[col.key];
    const formatted = renderFormattedTableCell(raw, col.displayAs);
    if (formatted !== null) return formatted;
    if (isTimestampMetricField(col.sourceField)) {
      return formatEventTimestampLocal(raw);
    }
    if (col.numeric) {
      const n = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(n) ? n.toLocaleString() : '';
    }
    return raw === null || raw === undefined ? '' : String(raw);
  };

  const columnCellSx = (col: ColumnMeta) => {
    if (col.displayAs === 'url_actions') {
      return { minWidth: 96, width: 96, whiteSpace: 'nowrap' as const };
    }
    if (col.displayAs === 'comma_multiline') {
      return { minWidth: 220, verticalAlign: 'top' as const };
    }
    return { minWidth: 140, verticalAlign: 'top' as const };
  };

  return (
    <Card
      variant="outlined"
      /*
       * `minWidth: 0` lets the card shrink below its intrinsic content
       * width when its grid/flex parent is narrower (e.g. mobile view).
       * Together with `overflow: 'hidden'` it prevents wide tables from
       * pushing the card past the viewport — the inner TableContainer
       * gets the horizontal scrollbar instead.
       */
      sx={{
        borderRadius: 2,
        height: '100%',
        minHeight: hideHeader ? 0 : panelMinHeight,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {!hideHeader ? (
        <>
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              bgcolor: (t) => reportsToolbarSurfaceBg(t),
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} component="div" sx={{ minWidth: 0 }}>
              {panelTitle}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              {contextBadge && (
                <Chip size="small" icon={<FilterAlt fontSize="small" />} label={contextBadge} color="secondary" />
              )}
              {headerActions}
            </Stack>
          </Box>

          {report.description && (
            <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {report.description}
              </Typography>
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 0.5, pt: 0.5, flexShrink: 0 }}>
          {headerActions}
        </Box>
      )}
      <CardContent
        sx={{
          flex: 1,
          pt: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{error}</Alert>}
        {downloadError && (
          <Alert
            severity={downloadError.includes('server limit') ? 'warning' : 'error'}
            sx={{ mb: 2, flexShrink: 0 }}
            onClose={() => setDownloadError(null)}
          >
            {downloadError}
          </Alert>
        )}

        {loading && isEmptyDataset && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && isEmptyDataset && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <StorageQuotaNoStats />
          </Box>
        )}

        {!isEmptyDataset && (
          <>
        {/*
          Table body scrolls inside the card; pagination stays pinned below so
          it remains visible in constrained layouts (e.g. drill-down drawer).
        */}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 1,
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100%',
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.numeric ? 'right' : 'left'}
                    sx={getInfralytiqsReportTableHeaderCellSx(theme, {
                      fontWeight: 700,
                      verticalAlign: 'top',
                      ...columnCellSx(col),
                    })}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <TableSortLabel
                        active={sortKey === col.sortKey}
                        direction={sortKey === col.sortKey ? (sortDir === 'ASC' ? 'asc' : 'desc') : 'asc'}
                        onClick={() => handleSort(col.sortKey)}
                      >
                        {col.label}
                      </TableSortLabel>
                      {col.filterable ? (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          placeholder="Filter"
                          value={columnFilters[col.key] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setColumnFilters((f) => ({ ...f, [col.key]: v }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: columnFilters[col.key] ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearFilter(col.key)}
                                  aria-label={`clear ${col.label} filter`}
                                >
                                  <Clear fontSize="inherit" />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          sx={{
                            '& .MuiInputBase-input': { py: 0.75, fontSize: 12 },
                            '& .MuiOutlinedInput-root': { borderRadius: 1 },
                          }}
                        />
                      ) : col.kind === 'metric' ? (
                        <TextField
                          size="small"
                          fullWidth
                          variant="outlined"
                          type="number"
                          placeholder="≥ min"
                          value={metricFilters[col.key] || ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setMetricFilters((f) => ({ ...f, [col.key]: v }));
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <FilterAlt fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: metricFilters[col.key] ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => clearMetricFilter(col.key)}
                                  aria-label={`clear ${col.label} filter`}
                                >
                                  <Clear fontSize="inherit" />
                                </IconButton>
                              </InputAdornment>
                            ) : undefined,
                          }}
                          sx={{
                            '& .MuiInputBase-input': { py: 0.75, fontSize: 12 },
                            '& .MuiOutlinedInput-root': { borderRadius: 1 },
                          }}
                        />
                      ) : (
                        <Box sx={{ height: 32 }} aria-hidden />
                      )}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4, border: 0 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                    No data matches the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={clickable ? () => onRowClick?.(row, report) : undefined}
                  sx={clickable ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.numeric ? 'right' : 'left'} sx={columnCellSx(col)}>
                      {renderCell(row, col)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          showFirstButton
          showLastButton
          labelRowsPerPage="Rows:"
          sx={{
            flexShrink: 0,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            '.MuiTablePagination-toolbar': { flexWrap: 'wrap', gap: 0.5, minHeight: 52 },
          }}
        />
          </>
        )}
      </CardContent>
    </Card>
  );
};
